/**
 * Route-level permission gate — ANY-of semantics, the OR counterpart to
 * `PermissionRoute.tsx`'s ALL-of gate. Introduced for the Appointment
 * Workstation single-page consolidation: the old 6 sub-routes each
 * required a different one of the 8 `AppointmentPermissions`, so a
 * user with e.g. only `appointment:queue:manage` could still open the
 * Queue page. Requiring all 8 (or hardcoding just `appointment:book`)
 * at the single consolidated route would silently lock such a user out
 * entirely — this preserves the old any-one-of-8 reachability at the
 * route level. Each panel inside the page still gates itself by its
 * own real, specific permission via `PermissionGuard`, so nobody is
 * ever shown an action their role can't actually perform — this only
 * controls whether the page renders at all.
 */
import type { JSX } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '@store/auth.store';

export interface AnyPermissionRouteProps {
  anyOf: string[];
}

export function AnyPermissionRoute({ anyOf }: AnyPermissionRouteProps): JSX.Element {
  const permissions = useAuthStore((state) => state.permissions);
  const allowed = anyOf.some((permission) => permissions.includes(permission));

  if (!allowed) {
    return <Navigate to="/403" replace />;
  }

  return <Outlet />;
}
